import {
  Autocomplete,
  Box,
  Card,
  CircularProgress,
  InputAdornment,
  OutlinedInput,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { shouldForwardProp } from '@mui/system';
import { visuallyHidden } from '@mui/utils';
import getAxiosInstance from 'api';
import moment from 'moment';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  border: '1px solid #DEDEDE',
  color: '#212120',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: '500',
}));

const OutlineInputStyle = styled(OutlinedInput, { shouldForwardProp })(
  ({ theme }) => ({
    width: 350,
    marginLeft: 16,
    paddingLeft: 16,
    paddingRight: 16,
    borderRadius: '8px',
    border: '1px solid rgba(33, 33, 32, 0.10)',
    background: '#FFFFFF',

    '& input': {
      background: 'transparent !important',
      paddingLeft: '4px !important',
    },
    [theme.breakpoints.down('lg')]: {
      width: 250,
    },
    [theme.breakpoints.down('md')]: {
      width: '100%',
      marginLeft: 4,
      background:
        theme.palette.mode === 'dark' ? theme.palette.dark[800] : '#fff',
    },
  })
);
const headCells = [
  {
    id: 'product',
    numeric: false,
    disablePadding: false,
    label: 'product',
  },
  {
    id: 'createdAt',
    numeric: true,
    disablePadding: false,
    label: 'Called at',
  },
];
const ProductType = [
  {
    label: 'All',
    value: '',
  },
  {
    label: 'Direct Debit',
    value: 'Direct Debit',
  },
  {
    label: 'BVN',
    value: 'BVN',
  },
  {
    label: 'Credit Assessment',
    value: 'Credit Assessment',
  },
];

function EnhancedTableHead({
  onSelectAllClick,
  order,
  orderBy,
  numSelected,
  rowCount,
  onRequestSort,
}) {
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  const StyledTableCellHead = styled(TableCell)(({ theme }) => ({
    backgroundColor: '#F0F0F0',
    color: 'rgba(33, 33, 32, 0.60)',
    border: '1px solid #DEDEDE',
  }));

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <StyledTableCellHead
            key={headCell.id}
            sortDirection={orderBy === headCell.id ? order : undefined}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order.toLowerCase() : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </StyledTableCellHead>
        ))}
      </TableRow>
    </TableHead>
  );
}
const TransactionHistoryList = ({ selectedDate }) => {
  const { id } = useParams();
  const theme = useTheme();
  const [order, setOrder] = useState('DESC');
  const [orderBy, setOrderBy] = useState('createdAt');
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [dense] = useState(false);
  const [limit, setLimit] = useState(10);
  const [anchorEl, setAnchorEl] = useState(null);
  const [total, setTotal] = useState();
  const [historyList, setHistoryList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [filter, setFilter] = useState({
    product: ProductType[0],
  });

  useEffect(() => {
    fetchData();
  }, [page, limit, filter, orderBy, order, selectedDate]);
  const fetchData = () => {
    setLoading(true);
    const url = 'getConsentHistoryByBusinessPartner';

    getAxiosInstance(
      `adminOrchestrator/dashboard/${url}`,
      'post',
      {
        startDate: moment(selectedDate[0]).format('MM-DD-YYYY'),
        endDate: moment(selectedDate[1]).format('MM-DD-YYYY'),
        businessPartnerId: id,
        page: page + 1,
        limit: limit,
        filter: {
          product: filter.product?.value || ProductType[0].value,
        },
        sortBy: orderBy,
        sortDirection: order,
      },
      { 'journey-type': 'Dashboard' }
    )
      .then((res) => {
        if (res?.data?.codes === 200) {
          setHistoryList(res?.data?.data?.items);
          setTotal(res?.data?.data?.total);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
        setHistoryList([]);
        setTotal(0);
        toast.error(err?.response?.data?.message || 'Error fetching data');
      });
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event?.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'ASC';
    setOrder(isAsc ? 'DESC' : 'ASC');
    setOrderBy(property);
  };
  const isSelected = () => {};
  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setLimit(event.target.value);
  };

  const handleFilterChange = (key, newValue) => {
    setFilterLoading(true);
    setFilter({ ...filter, [key]: newValue || ProductType[0] });
    setTimeout(() => {
      setFilterLoading(false);
    }, 300);
  };

  return (
    <>
      <Stack
        direction={'row'}
        flexWrap={'wrap'}
        justifyContent={'space-between'}
        alignItems={'center'}
      >
        <Typography variant="h3">Transaction History</Typography>

        <Stack
          direction={'row'}
          flexWrap={'wrap'}
          justifyContent={'space-between'}
          alignItems={'center'}
          gap={2}
        >
          <Autocomplete
            options={ProductType}
            getOptionLabel={(option) => option.label}
            value={filter.product}
            onChange={(event, newValue) =>
              handleFilterChange('product', newValue)
            }
            renderInput={(params) => (
              <TextField {...params} variant="outlined" />
            )}
            loading={filterLoading}
          />
        </Stack>
      </Stack>

      <Card sx={{ mt: 2 }}>
        <>
          {/* <EnhancedTableToolbar numSelected={selected.length} /> */}

          {/* table */}
          {loading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                height: '100px',
                alignItems: 'center',
              }}
            >
              <CircularProgress color="secondary" />
            </Box>
          ) : (
            <TableContainer>
              <Table
                sx={{
                  minWidth: 750,
                  backgroundColor: 'white',
                }}
                aria-labelledby="tableTitle"
                size={dense ? 'small' : 'medium'}
              >
                <EnhancedTableHead
                  numSelected={selected.length}
                  order={order}
                  orderBy={orderBy}
                  // onSelectAllClick={handleSelectAllClick}
                  onRequestSort={handleRequestSort}
                  rowCount={total}
                />
                {console.log('historyList', historyList)}
                <TableBody>
                  {historyList.length ? (
                    <>
                      {historyList.map((row, index) => {
                        if (typeof row === 'number') return null;
                        const isItemSelected = isSelected(row.name);
                        const labelId = `enhanced-table-checkbox-${index}`;

                        return (
                          <TableRow
                            hover
                            onClick={(event) => handleClick(event, row.name)}
                            role="checkbox"
                            aria-checked={isItemSelected}
                            tabIndex={-1}
                            key={row.name}
                            selected={isItemSelected}
                          >
                            <StyledTableCell
                              component="th"
                              id={labelId}
                              scope="row"
                              padding="normal"
                            >
                              {row?.product}
                            </StyledTableCell>
                            <StyledTableCell align="left">
                              {moment(row?.createdAt).format(
                                'MMM DD, YYYY hh:mm A'
                              )}
                            </StyledTableCell>
                          </TableRow>
                        );
                      })}
                    </>
                  ) : (
                    <></>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* table pagination */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={total}
            rowsPerPage={limit}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      </Card>
    </>
  );
};

export default TransactionHistoryList;
